import { Form, Input, InputNumber, Switch, Select, Button, Card, App, Space, Typography, Alert } from 'antd';
import { SaveOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { patch } from '../../../api/client';

export interface FieldSpec {
  key: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'textarea';
  default: string | number | boolean;
  sensitive?: boolean;
  /**
   * select 的可选值，后端已拼成「存储值 + 显示名」：库里和接口里存的仍是 value，
   * 界面上的中文来自 label。显示名不能在后台前端按 optionLabels 现查——
   * 响应拦截器会把对象键名递归 camelCase，month_extension 这类带下划线的存储值
   * 一旦被当作键转换过就对不上号，所以它只在数组的值里待着。
   */
  options?: { value: string; label: string }[];
  placeholder?: string;
  /** 配置项使用方法提示（label 右侧问号图标） */
  tip?: string;
  showWhen?: { field: string; equals: string | number | boolean };
}

export interface NamespaceConfig {
  name: string;
  title: string;
  description: string;
  fields: FieldSpec[];
  values: Record<string, string | number | boolean>;
}

/**
 * 通用命名空间配置表单：
 * 由后端返回的 fields schema 动态渲染，showWhen 实现字段联动显隐，
 * 敏感字段回显脱敏值（****xxxx），原样提交时后端自动保留原值。
 * onlyKeys/excludeKeys 用于在不同页面只渲染部分字段（如注册流程页只展示业务开关、凭据页只展示密钥），
 * 未渲染字段不提交，后端保留原值。
 */
export function NamespaceConfigForm({
  config,
  extraActions,
  onlyKeys,
  excludeKeys,
  emptyHint,
  switchCardKeys,
  labelOverrides,
  cardTitle,
  ignoreShowWhen,
}: {
  config: NamespaceConfig;
  extraActions?: React.ReactNode;
  onlyKeys?: string[];
  excludeKeys?: string[];
  emptyHint?: React.ReactNode;
  switchCardKeys?: string[];
  labelOverrides?: Record<string, { label?: string; desc?: string }>;
  cardTitle?: string;
  ignoreShowWhen?: boolean;
}) {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [saving, setSaving] = useState(false);

  const watched = Form.useWatch([], form) ?? {};
  const visibleFields = config.fields.filter((field) => {
    if (onlyKeys && !onlyKeys.includes(field.key)) return false;
    if (excludeKeys && excludeKeys.includes(field.key)) return false;
    if (!field.showWhen || ignoreShowWhen) return true;
    return watched[field.showWhen.field] === field.showWhen.equals;
  });
  const switchCards = switchCardKeys ? visibleFields.filter((f) => switchCardKeys.includes(f.key)) : [];
  const restFields = switchCardKeys ? visibleFields.filter((f) => !switchCardKeys.includes(f.key)) : visibleFields;

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      // 只提交可见字段，避免联动隐藏字段误提交
      const payload: Record<string, unknown> = {};
      for (const field of visibleFields) {
        payload[field.key] = values[field.key];
      }
      await patch(`/admin/system-config/${config.name}`, payload);
      message.success(`${config.title} 配置已保存`);
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) return;
      message.error(error instanceof Error ? error.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (visibleFields.length === 0 && emptyHint) {
    return (
      <Card title={cardTitle ?? config.title}>
        <Alert type="info" showIcon title={emptyHint} />
      </Card>
    );
  }

  return (
    <Card
      title={cardTitle ?? config.title}
      extra={
        <Space>
          {extraActions}
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            保存配置
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        {config.description}
      </Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        initialValues={config.values}
        style={{ maxWidth: 560 }}
      >
        {switchCards.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 12,
              marginBottom: 16,
            }}
          >
            {switchCards.map((f) => {
              const ov = labelOverrides?.[f.key];
              return (
                <div
                  key={f.key}
                  style={{
                    border: '1px solid #e6f0ee',
                    borderRadius: 12,
                    padding: '12px 14px',
                    background: '#f0fbfa',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13.5, color: '#0f766e' }}>{ov?.label ?? f.label}</span>
                    <Form.Item name={f.key} valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                  </div>
                  <div style={{ fontSize: 12, color: '#5b6468', marginTop: 6, lineHeight: 1.5 }}>
                    {ov?.desc ?? f.tip}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {restFields.map((field) => {
          // 与上面开关卡一致：labelOverrides 对普通字段也要生效，否则页面写的标题被后端下发的 label 盖掉
          const ov = labelOverrides?.[field.key];
          return (
            <Form.Item
              key={field.key}
              name={field.key}
              label={ov?.label ?? field.label}
              tooltip={field.tip ? { title: field.tip, icon: <QuestionCircleOutlined /> } : undefined}
              valuePropName={field.type === 'boolean' ? 'checked' : 'value'}
              extra={ov?.desc ?? (field.sensitive ? '已加密存储；回显为脱敏值（**** 开头），保持不变则沿用原值' : undefined)}
            >
              {field.type === 'boolean' ? (
                <Switch />
              ) : field.type === 'number' ? (
                <InputNumber style={{ width: '100%' }} placeholder={field.placeholder} />
              ) : field.type === 'select' ? (
                <Select options={field.options ?? []} placeholder={field.placeholder} />
              ) : field.type === 'textarea' ? (
                <Input.TextArea rows={10} placeholder={field.placeholder} />
              ) : (
                <Input
                  type={field.sensitive ? 'password' : 'text'}
                  placeholder={field.placeholder ?? (field.sensitive ? '留空或保持脱敏值不变' : '')}
                  autoComplete="new-password"
                />
              )}
            </Form.Item>
          );
        })}
      </Form>
    </Card>
  );
}


